import type { ComponentType } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { ForgotPasswordPage, ResetPasswordPage } from '../features/auth/PasswordPages';
import { LoginPage } from '../features/auth/LoginPage';
import { OAuthCompletePage } from '../features/auth/OAuthCompletePage';
import { SignUpPage } from '../features/auth/SignUpPage';
import { TwoFactorPage } from '../features/auth/TwoFactorPage';
import { VerifyEmailPage } from '../features/auth/VerifyEmailPage';
import { AcceptInvitationPage } from '../features/workspaces/AcceptInvitationPage';
import { CreateWorkspacePage } from '../features/workspaces/CreateWorkspacePage';
import { AppShell } from './AppShell';
import {
  LegacyBillingRedirect,
  RequireAuth,
  RequireGuest,
  RootRedirect,
  RouteError,
  WorkspaceLayout,
} from './guards';

/** Route-level code splitting: each screen loads on first visit. */
function page<M extends Record<string, unknown>>(load: () => Promise<M>, name: keyof M & string) {
  return async () => {
    const m = await load();
    return { Component: m[name] as ComponentType };
  };
}

export const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      {
        element: <RequireGuest />,
        children: [
          { path: '/login', element: <LoginPage /> },
          { path: '/signup', element: <SignUpPage /> },
          { path: '/forgot-password', element: <ForgotPasswordPage /> },
        ],
      },
      { path: '/verify-email', element: <VerifyEmailPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },
      { path: '/two-factor', element: <TwoFactorPage /> },
      { path: '/auth/two-factor', element: <Navigate to="/two-factor" replace /> },
      { path: '/auth/complete', element: <OAuthCompletePage /> },
      // Email unsubscribe links work signed out (FR-NOT-07).
      // Public plans and prices from the catalog (§5.13).
      {
        path: '/pricing',
        lazy: page(() => import('../features/billing/PricingPage'), 'PublicPricingPage'),
      },
      {
        path: '/notifications/unsubscribe',
        lazy: page(() => import('../features/notifications/UnsubscribePage'), 'UnsubscribePage'),
      },
      {
        element: <RequireAuth />,
        children: [
          { path: '/', element: <RootRedirect /> },
          // Linked from the MCP server metadata (resource_documentation).
          { path: '/settings/mcp', element: <RootRedirect suffix="/mcp" /> },
          { path: '/invitations/accept', element: <AcceptInvitationPage /> },
          // LINE account link from Smoke's LINE message (FR-NOT-11).
          // GitHub App setup callback without a valid state (no workspace known).
          {
            path: '/integrations/github',
            element: <RootRedirect suffix="/integrations/github?error=state_invalid" />,
          },
          // Staff console (FR-ADM-02..04): a separate area, staff role and 2FA required.
          {
            path: '/staff',
            lazy: page(() => import('../features/staff/StaffLayout'), 'StaffLayout'),
            children: [
              {
                index: true,
                lazy: page(() => import('../features/staff/StaffOverviewPages'), 'SyncHealthPage'),
              },
              {
                path: 'false-positives',
                lazy: page(
                  () => import('../features/staff/StaffOverviewPages'),
                  'FalsePositivesPage',
                ),
              },
              {
                path: 'mitigation-reviews',
                lazy: page(
                  () => import('../features/staff/StaffMitigationReviewPages'),
                  'MitigationReviewsPage',
                ),
              },
              {
                path: 'mitigation-reviews/:intelId',
                lazy: page(
                  () => import('../features/staff/StaffMitigationReviewPages'),
                  'MitigationReviewPage',
                ),
              },
              {
                path: 'ai-usage',
                lazy: page(() => import('../features/staff/StaffOverviewPages'), 'AiUsagePage'),
              },
              {
                path: 'alerts',
                lazy: page(() => import('../features/staff/StaffOverviewPages'), 'StaffAlertsPage'),
              },
              {
                path: 'workspaces',
                lazy: page(
                  () => import('../features/staff/StaffWorkspacePages'),
                  'StaffWorkspacesPage',
                ),
              },
              {
                path: 'workspaces/:workspaceId',
                lazy: page(
                  () => import('../features/staff/StaffWorkspacePages'),
                  'StaffWorkspacePage',
                ),
              },
              {
                path: 'flags',
                lazy: page(
                  () => import('../features/staff/StaffSettingsPages'),
                  'FeatureFlagsPage',
                ),
              },
              {
                path: 'service',
                lazy: page(
                  () => import('../features/staff/StaffServiceSwitchesPage'),
                  'StaffServiceSwitchesPage',
                ),
              },
              {
                path: 'users',
                lazy: page(() => import('../features/staff/StaffSettingsPages'), 'StaffUsersPage'),
              },
              {
                path: 'feedback',
                lazy: page(
                  () => import('../features/staff/StaffFeedbackPages'),
                  'StaffFeedbackListPage',
                ),
              },
              {
                path: 'feedback/:id',
                lazy: page(
                  () => import('../features/staff/StaffFeedbackPages'),
                  'StaffFeedbackPage',
                ),
              },
            ],
          },
          {
            path: '/notifications/line-link',
            lazy: page(() => import('../features/notifications/LineLinkPage'), 'LineLinkPage'),
          },
          {
            element: <AppShell />,
            children: [
              { path: '/workspaces/new', element: <CreateWorkspacePage /> },
              {
                path: '/account',
                lazy: page(() => import('../features/account/AccountPage'), 'AccountPage'),
              },
              // FR-ACC-13: questions, suggestions and feedback to Smoke.
              {
                path: '/feedback',
                lazy: page(() => import('../features/feedback/FeedbackPage'), 'FeedbackPage'),
              },
              // FR-BIL-16: billing belongs to the account.
              {
                path: '/account/billing',
                lazy: page(() => import('../features/billing/BillingPage'), 'BillingPage'),
              },
              {
                path: '/account/billing/plans',
                lazy: page(() => import('../features/billing/PricingPage'), 'AccountPricingPage'),
              },
              {
                path: '/w/:workspaceId',
                element: <WorkspaceLayout />,
                children: [
                  {
                    index: true,
                    lazy: page(
                      () => import('../features/dashboard/DashboardPage'),
                      'DashboardPage',
                    ),
                  },
                  {
                    path: 'findings',
                    lazy: page(() => import('../features/findings/FindingsPage'), 'FindingsPage'),
                  },
                  {
                    path: 'findings/:findingId',
                    lazy: page(
                      () => import('../features/findings/FindingDetailPage'),
                      'FindingDetailPage',
                    ),
                  },
                  {
                    path: 'clients',
                    lazy: page(() => import('../features/catalog/ClientsPage'), 'ClientsPage'),
                  },
                  {
                    path: 'projects/:projectId',
                    lazy: page(() => import('../features/catalog/ProjectPage'), 'ProjectPage'),
                  },
                  {
                    path: 'projects/:projectId/mcp',
                    lazy: page(() => import('../features/catalog/McpSetupPage'), 'McpSetupPage'),
                  },
                  {
                    path: 'mcp',
                    lazy: page(() => import('../features/catalog/McpIndexPage'), 'McpIndexPage'),
                  },
                  {
                    path: 'assets/:assetId',
                    lazy: page(() => import('../features/catalog/AssetPage'), 'AssetPage'),
                  },
                  {
                    path: 'eol',
                    lazy: page(() => import('../features/dashboard/EolPage'), 'EolPage'),
                  },
                  {
                    path: 'members',
                    lazy: page(() => import('../features/workspaces/MembersPage'), 'MembersPage'),
                  },
                  {
                    path: 'settings',
                    lazy: page(
                      () => import('../features/settings/WorkspaceSettingsPage'),
                      'WorkspaceSettingsPage',
                    ),
                  },
                  {
                    path: 'settings/notifications',
                    lazy: page(
                      () => import('../features/notifications/NotificationsPage'),
                      'NotificationsPage',
                    ),
                  },
                  {
                    path: 'api-tokens',
                    lazy: page(() => import('../features/settings/ApiTokensPage'), 'ApiTokensPage'),
                  },
                  {
                    path: 'reports',
                    lazy: page(() => import('../features/reports/ReportsPage'), 'ReportsPage'),
                  },
                  {
                    path: 'integrations/github',
                    lazy: page(() => import('../features/integrations/GithubPage'), 'GithubPage'),
                  },
                  // Old links and checkouts started before billing moved to the account.
                  { path: 'billing', element: <LegacyBillingRedirect /> },
                  {
                    path: 'billing/plans',
                    lazy: page(() => import('../features/billing/PricingPage'), 'PricingPage'),
                  },
                  {
                    path: 'audit',
                    lazy: page(() => import('../features/settings/AuditLogPage'), 'AuditLogPage'),
                  },
                ],
              },
            ],
          },
        ],
      },
      { path: '*', element: <RouteError notFound /> },
    ],
  },
]);
